import {
  Headphones, Hand, Lightbulb, Tv, Lock, Battery, Ear, Package,
  Droplet, Sparkles, RotateCcw, X, Sun, Moon, Vibrate, Glasses,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useStore, PRODUCTS, RING_VARIANTS } from "@/store/useStore";

/* ---- shared bits ---- */
function ActionBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`pill flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left text-xs font-medium ${
        active
          ? "border-[#d4af37]/60 bg-[#d4af37]/15 text-[#f3dd8f] shadow-[0_0_18px_rgba(212,175,55,0.25)]"
          : "border-white/10 bg-white/[0.03] text-slate-200 hover:border-[#d4af37]/40 hover:bg-white/[0.06]"
      }`}
    >
      <Icon className="h-4 w-4 shrink-0" style={{ color: active ? "#e6c65a" : "#9fb2c9" }} />
      <span>{label}</span>
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3 border-t border-white/5 pt-4">
      <div className="eyebrow violet-text">{title}</div>
      {children}
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="spec-val text-xs cyan-text">{value}</span>
    </div>
  );
}

/* ---- AirPods ---- */
function AirpodsPanel() {
  const { lidOpen, leftOut, rightOut, pickPod, setLid,
    spatialAudio, setSpatialAudio, anc, setAnc, eq, setEq } = useStore();
  const ancModes = [
    { id: "anc", label: "ANC" }, { id: "transparency", label: "Transp." }, { id: "adaptive", label: "Adaptive" },
  ];
  return (
    <>
      <div className="grid grid-cols-1 gap-2">
        <ActionBtn testid="airpods-select-left" icon={Headphones} label="Pick Up Left Pod"
          active={leftOut} onClick={() => pickPod("left")} />
        <ActionBtn testid="airpods-select-right" icon={Headphones} label="Pick Up Right Pod"
          active={rightOut} onClick={() => pickPod("right")} />
        <ActionBtn testid="airpods-select-both" icon={Headphones} label="Pick Up Both Pods"
          active={leftOut && rightOut} onClick={() => pickPod("both")} />
        <ActionBtn testid="airpods-close-case" icon={Package} label="Close Case"
          active={!lidOpen} onClick={() => setLid(false)} />
        <ActionBtn testid="airpods-open-case" icon={Ear} label="Open Case"
          active={lidOpen} onClick={() => setLid(true)} />
      </div>

      <Section title="Spatial Audio Equalizer">
        <div data-testid="airpods-spatial-equalizer" className="space-y-3">
          {["bass", "mid", "treble", "spatial"].map((k) => (
            <div key={k}>
              <div className="mb-1 flex justify-between text-xs">
                <span className="capitalize text-slate-300">{k === "spatial" ? "Spatial Field" : k}</span>
                <span className="spec-val cyan-text">{eq[k]}</span>
              </div>
              <Slider value={[eq[k]]} min={0} max={100} step={1} onValueChange={([v]) => setEq(k, v)} />
            </div>
          ))}
          <div className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
            <span className="text-xs text-slate-300">Spatial Audio</span>
            <Switch checked={spatialAudio} onCheckedChange={setSpatialAudio} data-testid="airpods-spatial-toggle" />
          </div>
        </div>
      </Section>

      <Section title="Noise Control">
        <div className="grid grid-cols-3 gap-2" data-testid="airpods-anc-toggle">
          {ancModes.map((m) => (
            <button key={m.id} onClick={() => setAnc(m.id)} data-testid={`airpods-anc-${m.id}`}
              className={`pill rounded-lg border px-2 py-2 text-[0.72rem] ${
                anc === m.id ? "pill-active-violet border-transparent" : "border-white/10 bg-white/[0.03] text-slate-300"
              }`}>{m.label}</button>
          ))}
        </div>
      </Section>

      <Section title="Battery & Compatibility">
        <Spec label="Left Earbud" value="98%" />
        <Spec label="Right Earbud" value="96%" />
        <Spec label="MagSafe Case" value="84%" />
        <div className="flex flex-wrap gap-1.5 pt-1">
          {["iPhone 16", "iPad Pro", "Apple TV", "Vision Pro"].map((d) => (
            <span key={d} className="mono rounded-full border border-cyan-400/20 bg-cyan-400/5 px-2 py-0.5 text-[0.72rem] cyan-text">{d}</span>
          ))}
        </div>
      </Section>
    </>
  );
}

/* ---- L'Oréal Melasyl ---- */
function MelasylPanel() {
  const { melasylState, setMelasylState, routine, setRoutine, skinIntensity, setSkinIntensity, fitz, setFitz } = useStore();
  return (
    <>
      <div className="grid grid-cols-1 gap-2">
        <ActionBtn testid="melasyl-open-bottle" icon={Package} label="Open Bottle"
          active={melasylState !== "closed"} onClick={() => setMelasylState("open")} />
        <ActionBtn testid="melasyl-dropper-trigger" icon={Droplet} label="Pick Up Dropper"
          active={melasylState === "dropper" || melasylState === "applied"} onClick={() => setMelasylState("dropper")} />
        <ActionBtn testid="melasyl-apply-skin" icon={Sparkles} label="Apply to Skin"
          active={melasylState === "applied"} onClick={() => setMelasylState("applied")} />
        <ActionBtn testid="melasyl-reset" icon={RotateCcw} label="Reset"
          active={false} onClick={() => setMelasylState("closed")} />
      </div>

      <Section title="Day / Night Routine Builder">
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="melasyl-routine-morning" onClick={() => setRoutine("day")}
            className={`pill flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs ${
              routine === "day" ? "pill-active-cyan border-transparent" : "border-white/10 bg-white/[0.03] text-slate-300"}`}>
            <Sun className="h-4 w-4" /> Day</button>
          <button data-testid="melasyl-routine-night" onClick={() => setRoutine("night")}
            className={`pill flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs ${
              routine === "night" ? "pill-active-violet border-transparent" : "border-white/10 bg-white/[0.03] text-slate-300"}`}>
            <Moon className="h-4 w-4" /> Night</button>
        </div>
        <div className="space-y-1.5 pt-1">
          {(routine === "day"
            ? ["Gentle Cleanser", "Melasyl Serum 2%", "Niacinamide Shield", "SPF 50+ Fluid"]
            : ["Double Cleanse", "Melasyl Restoration", "Retinal 0.3%", "Repair Night Balm"]
          ).map((s, i) => (
            <div key={s} className="flex items-center gap-2 text-xs text-slate-300">
              <span className="mono flex h-5 w-5 items-center justify-center rounded-full border border-cyan-400/30 text-[0.72rem] cyan-text">{i + 1}</span>
              {s}
            </div>
          ))}
        </div>
      </Section>

      <Section title="AR Pigmentation Zones">
        <div className="mb-1 flex justify-between text-xs">
          <span className="text-slate-300">Response Intensity</span>
          <span className="spec-val cyan-text">{skinIntensity}%</span>
        </div>
        <Slider value={[skinIntensity]} min={0} max={100} step={1}
          onValueChange={([v]) => setSkinIntensity(v)} data-testid="melasyl-skin-zone-slider" />
        <div className="pt-2">
          <div className="mb-1 flex justify-between text-xs">
            <span className="text-slate-300">Fitzpatrick Phototype</span>
            <span className="spec-val violet-text">{["I", "II", "III", "IV", "V", "VI"][fitz - 1]}</span>
          </div>
          <Slider value={[fitz]} min={1} max={6} step={1} onValueChange={([v]) => setFitz(v)} data-testid="melasyl-fitz-slider" />
        </div>
        <p className="pt-1 text-[0.72rem] leading-relaxed text-slate-500">
          Melasyl™ targets melanin precursors to reduce hyperpigmentation across all phototypes.
        </p>
      </Section>
    </>
  );
}

/* ---- Lotus Ring ---- */
function LotusPanel() {
  const { variant, setVariant, ringSize, setRingSize, tryOn, setTryOn,
    flashlight, tv, lock, toggleDevice, haptic, setHaptic, skinTone, setSkinTone,
    xrAR, xrVR } = useStore();
  const sizes = [6, 7, 8, 9, 10, 11, 12];
  const diameter = (15.7 + (ringSize - 6) * 0.82).toFixed(1);
  const haptics = [{ id: "pulse", l: "Pulse" }, { id: "wave", l: "Wave" }, { id: "sos", l: "SOS" }];
  const skins = [
    { id: "light", c: "#ecc6a6" }, { id: "medium", c: "#cf9d74" },
    { id: "tan", c: "#a9774f" }, { id: "deep", c: "#6d4733" },
  ];
  const xrReady = xrAR === true || xrVR === true;
  return (
    <>
      <div className="grid grid-cols-1 gap-2">
        {xrReady ? (
          <ActionBtn testid="lotus-try-on-button" icon={Hand} label={tryOn ? "Remove From Hand" : "Try On Ring"}
            active={tryOn} onClick={() => setTryOn(!tryOn)} />
        ) : (
          <div
            data-testid="lotus-tryon-reminder"
            className="flex items-start gap-2.5 rounded-xl border border-[#d4af37]/25 bg-[#d4af37]/[0.06] px-3 py-2.5 text-[0.68rem] leading-relaxed text-[#e6d49a]"
          >
            <Glasses className="mt-0.5 h-4 w-4 shrink-0 text-[#e6c65a]" />
            <span>Put on a VR/MR/AR headset to try the ring on immersively.</span>
          </div>
        )}
        <ActionBtn testid="lotus-gesture-lights" icon={Lightbulb} label={`Flashlight ${flashlight ? "On" : "Off"}`}
          active={flashlight} onClick={() => toggleDevice("flashlight")} />
        <ActionBtn testid="lotus-gesture-tv" icon={Tv} label={`TV ${tv ? "On" : "Off"}`}
          active={tv} onClick={() => toggleDevice("tv")} />
        <ActionBtn testid="lotus-gesture-lock" icon={Lock} label={`Smart Lock ${lock ? "Locked" : "Unlocked"}`}
          active={lock} onClick={() => toggleDevice("lock")} />
      </div>

      <Section title="Finish / Color">
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(RING_VARIANTS).map(([id, v]) => (
            <button key={id} onClick={() => setVariant(id)} data-testid={`lotus-swatch-${id === "rosegold" ? "rosegold" : id}`}
              className={`pill flex flex-col items-center gap-1.5 rounded-xl border px-2 py-2.5 ${
                variant === id ? "border-[#d4af37]/60 bg-white/[0.06]" : "border-white/10 bg-white/[0.03]"}`}>
              <span className="h-6 w-6 rounded-full border border-white/20" style={{ background: v.swatch }} />
              <span className="text-[0.72rem] text-slate-300">{v.label}</span>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Try-On Skin Tone">
        <div className="grid grid-cols-4 gap-2" data-testid="lotus-skin-tone">
          {skins.map((s) => (
            <button key={s.id} onClick={() => setSkinTone(s.id)} data-testid={`lotus-skin-${s.id}`}
              className={`pill flex flex-col items-center gap-1.5 rounded-xl border px-1 py-2 ${
                skinTone === s.id ? "border-[#d4af37]/60 bg-white/[0.06]" : "border-white/10 bg-white/[0.03]"}`}>
              <span className="h-5 w-5 rounded-full border border-white/20" style={{ background: s.c }} />
              <span className="text-[0.7rem] capitalize text-slate-300">{s.id}</span>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Ring Size Customizer">
        <div className="flex flex-wrap gap-1.5" data-testid="lotus-size-select">
          {sizes.map((s) => (
            <button key={s} onClick={() => setRingSize(s)} data-testid={`lotus-size-${s}`}
              className={`pill h-8 w-8 rounded-lg border text-xs ${
                ringSize === s ? "pill-active-cyan border-transparent" : "border-white/10 bg-white/[0.03] text-slate-300"}`}>{s}</button>
          ))}
        </div>
        <Spec label="US Size" value={`${ringSize}`} />
        <Spec label="Inner Diameter" value={`${diameter} mm`} />
      </Section>

      <Section title="Haptic Feedback Profile">
        <div className="grid grid-cols-3 gap-2">
          {haptics.map((h) => (
            <button key={h.id} onClick={() => setHaptic(h.id)} data-testid={`lotus-haptic-${h.id}`}
              className={`pill flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-[0.72rem] ${
                haptic === h.id ? "pill-active-violet border-transparent" : "border-white/10 bg-white/[0.03] text-slate-300"}`}>
              <Vibrate className="h-3.5 w-3.5" /> {h.l}</button>
          ))}
        </div>
      </Section>
    </>
  );
}

export default function ProductPanel({ mobile }) {
  const product = useStore((s) => s.product);
  const setMobilePanel = useStore((s) => s.setMobilePanel);
  const meta = PRODUCTS[product];
  const icons = { airpods: Headphones, melasyl: Droplet, lotus: Hand };
  const Icon = icons[product];

  return (
    <aside
      data-testid="product-interaction-panel"
      className={
        mobile
          ? "glass glass-violet rounded-t-3xl p-5 space-y-4"
          : "glass glass-violet fixed left-5 top-24 z-40 w-[21rem] max-h-[calc(100vh-13rem)] overflow-y-auto scroll-thin rounded-2xl p-5 space-y-4"
      }
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#d4af37] to-[#b8922b] text-[#16130a]">
            <Icon className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-sm font-semibold leading-none text-white">{meta.name}</h3>
            <span className="eyebrow text-[#d4af37]">{meta.tag}</span>
          </div>
        </div>
        {mobile && (
          <button onClick={() => setMobilePanel(null)} data-testid="close-product-mobile">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-white/5 pt-3">
        <span className="h-px w-5 bg-[#d4af37]/60" />
        <span className="eyebrow cyan-text">Interaction Menu</span>
        <span className="h-px flex-1 bg-white/5" />
      </div>
      {product === "airpods" && <AirpodsPanel />}
      {product === "melasyl" && <MelasylPanel />}
      {product === "lotus" && <LotusPanel />}
    </aside>
  );
}
